import React, { useRef, useState, useEffect } from 'react';
import { Button, Form, Col, Alert } from 'react-bootstrap';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import CreatableSelect from 'react-select/creatable';
import { BsReply, BsExclamationCircle, BsFileEarmarkText, BsChevronDown, BsChevronUp, BsPencilSquare, BsCheck2Square } from 'react-icons/bs';

import styles from './Comment.module.css';
import Attachments from '../../../Attachments';
import CustomEditor from '../../../Editor';
import { baseURL } from "../../../../helpers/baseURL";
import { headers } from '../../../../helpers/Requests';
import { ucFirst } from "../../../../helpers/generic";
import { imageOption } from '../../../SelectComponents';
import { resetChangedColumns } from "../../../../redux/actions";
import RecipientsSelect, { normalizeRecipients } from './RecipientsSelect';
import failAlert from "../../../../helpers/failAlert";
import QuickRepliesPicker from '../../../QuickRepliesPicker';
import {
    createEmptyQuickReplyComposeState,
    formatUnresolvedQuickReplyMessage,
    prepareQuickReplyComposeContent,
} from '../../../../helpers/quickReplyCompose';

const Comment = ({ type, activeKey, ticket, onComplete, onCommentAdded }) => {
	const { id: urlId } = useParams();
	const id = ticket?.id ?? urlId;
    const dispatch = useDispatch();
	const editor = useRef('');
	const quickReplyComposeRef = useRef(createEmptyQuickReplyComposeState());
	const [attachments, setAttachments] = useState([]);
	const [editCC, setEditCC] = useState(false);
	const [cc, setCC] = useState([]);
	const [expanded, setExpanded] = useState(true);
	const [isInvalid, setIsInvalid] = useState(false);
	const [to, setTo] = useState(null);
	const [requestResponse, setRequestResponse] = useState({
		isLoading: false,
		success: null,
		message: ''
	});
	const [editorResetKey, setEditorResetKey] = useState(0);
	const users = useSelector(state => state.usersReducer.users);
	const user = useSelector(state => state.userReducer?.user);

    const changedColumnsTaskSelector = useSelector(state => {
        return state?.UpdatesTaskReducer?.changedColumns;
    });

	useEffect(() => {
        if(["notes"].some(key => changedColumnsTaskSelector[key]) && type === 'note'){
            onComplete && onComplete();
            dispatch(resetChangedColumns());
        }
    },[changedColumnsTaskSelector]);

	useEffect(() => {
		if (activeKey === type) setExpanded(true)
	}, [activeKey])

	useEffect(() => {
		if (requestResponse.success !== null) {
			const timer = setTimeout(() => {
				setRequestResponse({
					isLoading: false,
					success: null,
					message: ''
				});
			}, 5000);
			return () => clearTimeout(timer);
		}
	}, [requestResponse.success]);

    useEffect(() => {
        setCC(normalizeRecipients(ticket?.recipients));
    }, [ticket?.recipients]);

	const formatUserOptions = (users) => {
		return users.map(row => ({
			value: row.email,
			label: ucFirst(row.name),
			image: row.profile_image,
			description: row.email
		}));
	};

    const updateRecipients = async (emails) => {
		const ccTemp = cc;
        try {
            setCC(emails);
            await axios.patch(`${baseURL}ticket-update/${ticket.id}`, { recipients: emails }, headers);
        } catch (e) {
			failAlert(e.response?.data?.message || 'CC update failed!');
			setCC(ccTemp);
        }
    };

    useEffect(() => {
        quickReplyComposeRef.current = createEmptyQuickReplyComposeState();
    }, [editorResetKey]);

	const handleEditorChange = () => {
		if (quickReplyComposeRef.current?.draft) {
			quickReplyComposeRef.current = {
				...quickReplyComposeRef.current,
				modified: true,
			};
		}
	};

	const handleFormSubmit = async () => {
		if (editor.current.getText().length === 0) {
			setIsInvalid(true);
			setRequestResponse({
				isLoading: false,
				success: false,
				message: 'Please enter a message!'
			});
			return;
		}
		if (type === 'forward' && !to) {
			setRequestResponse({
				isLoading: false,
				success: false,
				message: 'Please select a recipient!'
			});
			return;
		}

		const { content, unresolved } = prepareQuickReplyComposeContent({
			editorHtml: editor?.current?.getHTML(),
			quickReplyCompose: quickReplyComposeRef.current,
			ticket,
			user,
		});

		if (unresolved.length > 0) {
			setIsInvalid(true);
			setRequestResponse({
				isLoading: false,
				success: false,
				message: formatUnresolvedQuickReplyMessage(unresolved),
			});
			return;
		}

		const endpoint = type === 'forward' ? 'forward' : 'comments';
		const data = { type, content, attachments, to: type === 'forward' ? to : null };

		setIsInvalid(false);
		setRequestResponse({ isLoading: true, success: null, message: '' });
		try {
			const response = await axios.post(`${baseURL}${endpoint}/${id}`, data, headers);
			setRequestResponse({
				isLoading: false,
				success: true,
				message: response.data?.message ?? 'Posted successfully!'
			});
			editor.current.reset();
			setEditorResetKey((key) => key + 1);
			quickReplyComposeRef.current = createEmptyQuickReplyComposeState();
			setAttachments([]);
			if (onCommentAdded && response.data?.comment) {
				onCommentAdded(response.data.comment);
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message ?? 'Something went wrong!';
			setRequestResponse({
				isLoading: false,
				success: false,
				message: errorMessage
			});
		}
	};

	if (type === 'reply' && !ticket?.created_by?.email) {
		return <Alert variant='danger' className='mt-3'>You cannot reply to this ticket since its origin is unknown.</Alert>
	}

	return (
		<div className={styles.container + (expanded ? ` ${styles.expanded}` : '')}>
			{requestResponse.isLoading && <Alert variant="info">Loading...</Alert>}
			{requestResponse.success !== null && <Alert variant={requestResponse.success ? "success" : "danger"}>{requestResponse.message}</Alert>}
			<Button className={styles.tabToggle} variant="link" onClick={() => setExpanded(prev => !prev)}>{expanded ? <BsChevronUp /> : <BsChevronDown />}</Button>
			<Form className={styles.form}>
				{type === 'note' && <div className={styles.header}><BsExclamationCircle className='me-2' /> Notes are marked as <span className='px-1'>private</span> and can be seen only by staff members!</div>}
				{type === 'reply' && <>
				<div className={styles.header}>To: <u className='ms-2'>{ticket.created_by.email}</u></div>
                <div className={styles.header}>
                    CC: {cc?.length > 0 ? <u className='ms-2 flex-grow-1'>{cc.join(', ')}</u> : <span className='ms-2 flex-grow-1 text-secondary fs-8 opacity-50'>Empty</span>}
                    <Button className='ms-2' size='sm' variant={editCC ? 'primary' : 'link'} onClick={() => setEditCC(prev => !prev)}>{editCC ? <><BsCheck2Square /> Save</> : <BsPencilSquare />}</Button>
                </div>
                {editCC && (
                    <div className="mt-2">
                        <RecipientsSelect
                            selectedEmails={cc}
                            onChange={updateRecipients}
                        />
                    </div>
                )}
				</>}
				{type === 'forward' && (
					<>
						<div className={styles.header}><BsExclamationCircle className='me-2' /> This forwarded message will remain <span className='px-1'>private</span> and not be displayed in the conversation.</div>
						<div className="d-flex align-items-center mt-2">
							<span className="me-2">To:</span>
							<CreatableSelect
								isClearable
								onChange={(option) => setTo(option ? option.value : '')}
								options={formatUserOptions(users)}
								components={{ Option: imageOption }}
								className="flex-grow-1"
								classNamePrefix="select-component"
								placeholder="Type email address or select a recipient"
								value={to ? { label: to, value: to } : null}
								formatCreateLabel={userInput => `Forward to: ${userInput}`}
							/>
						</div>
					</>
				)}
				<Form.Group controlId="form.content" className='mt-2'>
					<CustomEditor
						id={'editor-' + type}
						placeholder={`Please add your ${type} here`}
						isInvalid={isInvalid}
						ref={editor}
						name="content"
						onChange={handleEditorChange}
					/>
				</Form.Group>
				<Col className={`d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2 ${styles.commentActions}`}>
					<div className="d-flex align-items-center gap-2 flex-wrap">
						<Attachments attachments={attachments} setAttachments={setAttachments} showAll={false} />
						{(type === 'note' || type === 'reply') && (
							<QuickRepliesPicker
								editorRef={editor}
								ticket={ticket}
								disabled={requestResponse.isLoading}
								editorResetKey={editorResetKey}
								composeRef={quickReplyComposeRef}
							/>
						)}
					</div>
					<Button
						onClick={handleFormSubmit}
						disabled={requestResponse.isLoading}
						style={{ flexShrink: 0, whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
					>
						{type === 'reply' && <><BsReply className="me-1 mb-1" size={15} /> Post Reply</>}
						{type === 'forward' && <><BsReply className="me-1 mb-1 flip-icon" size={15} /> Forward</>}
						{type === 'note' && <><BsFileEarmarkText className="me-1 mb-1" size={15} /> Save Note</>}
					</Button>
				</Col>
			</Form>
		</div>
	);
};

export default Comment;
